import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOperatorConfig } from '../hooks/useOperatorConfig';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { useAuth } from '../hooks/useAuth';
import useBreakpoint from '../hooks/useBreakpoint';
import type { ConnectionHealth } from '../types';

function StatusBadge({
  label,
  status,
  compact = false,
}: {
  label: string;
  status: ConnectionHealth;
  compact?: boolean;
}) {
  const palette: Record<ConnectionHealth, { background: string; border: string; color: string; dot: string }> = {
    connected: {
      background: 'rgba(34, 197, 94, 0.18)',
      border: 'rgba(34, 197, 94, 0.45)',
      color: '#bbf7d0',
      dot: '#22c55e',
    },
    connecting: {
      background: 'rgba(234, 179, 8, 0.16)',
      border: 'rgba(234, 179, 8, 0.45)',
      color: '#fde68a',
      dot: '#fbbf24',
    },
    disconnected: {
      background: 'rgba(239, 68, 68, 0.18)',
      border: 'rgba(239, 68, 68, 0.4)',
      color: '#fecaca',
      dot: '#f87171',
    },
  };

  const styles = palette[status];

  if (compact) {
    return (
      <span
        title={`${label}: ${status}`}
        aria-label={`${label}: ${status}`}
        style={{
          width: '0.6rem',
          height: '0.6rem',
          borderRadius: '9999px',
          background: styles.dot,
          display: 'inline-block',
          boxShadow: '0 0 0 3px rgba(15, 23, 42, 0.65), 0 0 8px rgba(15, 23, 42, 0.25)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderRadius: '9999px',
        padding: '0.35rem 0.85rem',
        background: styles.background,
        border: `1px solid ${styles.border}`,
        color: styles.color,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        boxShadow: '0 18px 35px -20px rgba(30, 64, 175, 0.55)',
      }}
    >
      <span
        style={{
          width: '0.45rem',
          height: '0.45rem',
          borderRadius: '9999px',
          background: styles.dot,
          boxShadow: `0 0 0 3px rgba(15, 23, 42, 0.65)`,
        }}
      />
      {label}
    </div>
  );
}

interface HeaderProps {
  onOpenSettings: () => void;
}

export default function Header({ onOpenSettings }: HeaderProps) {
  const [time, setTime] = useState(() => new Date());
  const { config } = useOperatorConfig();
  const { backend, livekit } = useConnectionStatus();
  const { logout, session, hasCapability } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();
  const operatorName = config.operatorName?.trim() || 'Operator';
  const operatorDisplayName =
    (session?.user.displayName?.trim() || session?.user.username || operatorName).trim() || 'Operator';
  const isAdmin = hasCapability('admin');
  const location = useLocation();
  const inAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const headerPadding = isMobile
    ? '0.4rem 0.9rem 0.35rem'
    : isTablet
      ? '1rem 1.5rem 0.75rem'
      : '1rem 1.75rem 0.75rem';

  const consoleTitle = isMobile
    ? 'A.R.E.S'
    : 'A.R.E.S - Anesthesia Remote Emergency Supervision - Operator Console';

  const headerLayout: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    gap: isMobile ? '0.75rem' : '1rem',
    padding: headerPadding,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(12px)',
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
  };

  const metaLayout: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: 'auto',
    flexWrap: isTablet && !isMobile ? 'wrap' : 'nowrap',
    gap: isMobile ? '0.65rem' : '1rem',
  };

  const statusGroupStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: isMobile ? '0.4rem' : '0.75rem',
    justifyContent: 'flex-end',
    alignItems: 'center',
  };

  const actionGroupStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    gap: isMobile ? '0.5rem' : '0.75rem',
    width: 'auto',
  };

  const timeStyle: CSSProperties = {
    textAlign: 'right',
    color: '#cbd5f5',
    fontVariantNumeric: 'tabular-nums',
    alignSelf: 'center',
    paddingTop: 0,
  };

  return (
    <header style={headerLayout}>
      <div
        style={{
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '0.25rem' : '0.35rem',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: isMobile ? '1rem' : '1.35rem',
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: isMobile ? '0.01em' : '0.005em',
          }}
        >
          {consoleTitle}
        </h1>
        {!isMobile && (
          <p
            style={{
              margin: '0.35rem 0 0',
              color: '#94a3b8',
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            Monitor incoming device calls and join instantly.
          </p>
        )}
        <p
          style={{
            margin: 0,
            color: '#cbd5f5',
            fontSize: isMobile ? '0.7rem' : '0.85rem',
            display: 'flex',
            flexDirection: 'row',
            gap: '0.5rem',
            alignItems: 'center',
          }}
        >
          <span>
            Operator: <span style={{ fontWeight: 600, color: '#f8fafc' }}>{operatorDisplayName}</span>
          </span>
        </p>
      </div>
      <div style={metaLayout}>
        <div style={statusGroupStyle}>
          <StatusBadge label="Backend" status={backend} compact={isMobile} />
          <StatusBadge label="LiveKit" status={livekit} compact={isMobile} />
        </div>
        <div style={{ ...actionGroupStyle, alignItems: 'center' }}>
          {isAdmin && inAdmin ? (
            <Link
              to="/calls"
              style={{
                border: '1px solid rgba(59, 130, 246, 0.45)',
                borderRadius: '9999px',
                padding: isMobile ? '0.45rem 0.75rem' : '0.6rem 1.2rem',
                background: 'rgba(37, 99, 235, 0.15)',
                color: '#bfdbfe',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                boxShadow: '0 20px 40px -25px rgba(59, 130, 246, 0.45)',
                fontSize: isMobile ? '0.65rem' : '0.75rem',
              }}
            >
              ← Operator Console
            </Link>
          ) : null}
          <button
            onClick={onOpenSettings}
            style={{
              border: '1px solid rgba(148, 163, 184, 0.4)',
              borderRadius: '9999px',
              padding: isMobile ? '0.45rem 0.75rem' : '0.6rem 1.2rem',
              background: 'rgba(15, 23, 42, 0.4)',
              color: '#cbd5f5',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: '0 20px 40px -25px rgba(37, 99, 235, 0.5)',
              minWidth: isMobile ? 'auto' : 'unset',
              fontSize: isMobile ? '0.65rem' : '0.75rem',
            }}
          >
            ⚙️ Settings
          </button>
          <button
            onClick={() => void logout()}
            style={{
              border: '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '9999px',
              padding: isMobile ? '0.45rem 0.75rem' : '0.6rem 1.2rem',
              background: 'rgba(239, 68, 68, 0.12)',
              color: '#fecaca',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: '0 20px 40px -25px rgba(239, 68, 68, 0.45)',
              minWidth: isMobile ? 'auto' : 'unset',
              fontSize: isMobile ? '0.65rem' : '0.75rem',
            }}
          >
            🚪 Sign out
          </button>
        </div>
        <div style={timeStyle}>
          <div
            style={{
              fontSize: isMobile ? '0.65rem' : '0.85rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#64748b',
            }}
          >
            {time.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <div style={{ fontSize: isMobile ? '1.1rem' : '1.5rem', fontWeight: 500 }}>{time.toLocaleTimeString()}</div>
        </div>
      </div>
    </header>
  );
}
